import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, onDocumentReady } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 *  @property {HTMLElement} defaultStateElement - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */

const suggestionsDiv = document.getElementById('suggested-queries');

class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton', 'defaultStateElement'];


  // Method to show the default element when no search results are shown
  #showDefaultElement() {
    if (this.refs.defaultStateElement) {
      this.refs.defaultStateElement.style.display = 'block';
     suggestionsDiv.innerHTML = ''; 
    }
    //  if (this.refs.search_page_tabs) {
    //   this.refs.search_page_tabs.style.display = 'none';
    // }
  }

  // Method to hide the default element when search results are shown
  #hideDefaultElement() {
    if (this.refs.defaultStateElement) {
      this.refs.defaultStateElement.style.display = 'none';
    }
    //      if (this.refs.search_page_tabs) {
    //   this.refs.search_page_tabs.style.display = 'flex';
    // }
  }

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      // this.style.setProperty('--predictive-search-results-height', `${entry.contentRect.height}px`);
    }
  });

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    } else {
      document.addEventListener(ThemeEvents.megaMenuHover, this.#blurSearch, { signal });
    }

    onDocumentReady(this.#getRecentlyViewed);

    const results = this.refs.predictiveSearchResults.firstElementChild;

    if (results) {
      this.#resizeObserver.observe(results);
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  expandSearch = () => {
    // Add the expanded class to the search component
    this.classList.add('predictive-search--expanded');
    if (this.dataset.activeColorScheme) {
      const target = this.dialog ?? this;
      target.classList.add(`color-${this.dataset.activeColorScheme}`);
    }
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter':
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
      url.searchParams.set('resources[type]', 'query,product,article');
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;
         console.log(resultsMarkup);
        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
        this.#hideDefaultElement(); 
        // disableBundleButtonssearch();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  /**
   * Fetch recently viewed products using the section renderer and update the results container.
   */
  #getRecentlyViewed = async () => {
    const { predictiveSearchResults } = this.refs;
    // Get the initial height before the results are rendered
    const abortController = this.#createAbortController();

    try {
      const resultsMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!resultsMarkup) return;

      const parsedNextPage = new DOMParser().parseFromString(resultsMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedNextPage.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = predictiveSearchResults.querySelector('#predictive-search-products');
      if (!collectionElement) return;

      if (this.refs.recentlyViewedWrapper) {
        this.refs.recentlyViewedWrapper.remove();
      }

      if (abortController.signal.aborted) return;
      // Prepend the recently viewed products to the collection
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    } catch (error) {
      throw error;
    }
  };

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
    this.#showDefaultElement(); 
  };

  /**
   * Closes the predictive search.
   */
  #blurSearch = () => {
    this.refs.searchInput.blur();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}

function highlightMatch(text, query) {
  const regex = new RegExp(`(${query})`, 'gi'); // case-insensitive match
  return text.replace(regex, '<mark>$1</mark>');
}
const searchInput = document.getElementById('cmdk-input');


  searchInput.addEventListener('input', function() {
    const query = this.value.trim();
    if (query.length === 0) {
      suggestionsDiv.innerHTML = '';
      return;
    }

    fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=query,product,article&resources[limit_scope]=each&section_id=predictive-search`)
      .then(response => response.json())
      .then(data => {
        const queries = data.resources.results.queries || [];
        if (queries.length === 0) {
          suggestionsDiv.innerHTML = '';
          return;
        }
       console.log(queries);
        suggestionsDiv.innerHTML = queries.map(q => {
        const highlightedTitle = highlightMatch(q.text, query);
         return `<li
                  class="predictive-search-results__card--query"
                  on:keydown="/onSearchKeyDown"><a class="pills__pill predictive-search-results__pill" href="${q.url}">
                  <span aria-label="${q.text}">
                  ${highlightedTitle}
                  </span> 
                  </a></li>`}).join(' ');

        // Attach click events to suggested queries
        document.querySelectorAll('.predictive-search-results__pill').forEach(el => {
          el.addEventListener('click', () => {
            searchInput.value = el.textContent;
            suggestionsDiv.innerHTML = '';
            // Optionally submit the form or trigger search here
          });
        });
      });
  });

document.addEventListener('click', function(e) {
  if (e.target.closest('.search-tab-heading')) {
    const heading = e.target.closest('.search-tab-heading');

    document.querySelectorAll('.search-tab-heading').forEach(h => h.classList.remove('active'));
    heading.classList.add('active');

    const targetId = heading.getAttribute('data-tab-target');

    document.querySelectorAll('.tab_search_content').forEach(tab => tab.classList.remove('active'));
    const targetTab = document.getElementById(targetId);
    if (targetTab) targetTab.classList.add('active');
  }
});

const headerSectionId = 'sections--18102980903025__header_section';

          function fetchSectionHtmlDirect() {
            const url = `${window.location.pathname}?section_id=${encodeURIComponent(headerSectionId)}`;

            return fetch(url, {
              method: 'GET',
              headers: {
                Accept: 'text/html',
              },
            }).then((res) => res.text());
          }
            
document.addEventListener('click', async function(e) {
  const btn = e.target.closest('.resource-card__add-to-cart');
  if (!btn) return;

  e.preventDefault();
  const variantId = btn.getAttribute('data-variant-id');
  btn.disabled = true;

  try {
    // Add product to cart
    const resp = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    });

    if (!resp.ok) {
      // alert('Failed to add to cart');
      btn.disabled = false;
      return;
    }

    // Import theme events module
    const { CartUpdateEvent } = await import('@theme/events');

    // Fetch updated cart
    const cartResp = await fetch('/cart.js');
    const cart = await cartResp.json();

    // Dispatch cart update event
    const event = new CartUpdateEvent(cart, 'manual-trigger', {
      itemCount: cart.item_count,
      source: 'add-to-cart-grid',
      sections: {}
    });
    document.dispatchEvent(event);

    // Wait a short delay to ensure theme handles event
    await new Promise(resolve => setTimeout(resolve, 450));

    fetchSectionHtmlDirect().then((html) => {
                if (!html) return;

                const temp = document.createElement('div');
                temp.innerHTML = html;

                // root of the returned section
                const newSectionRoot =
                  temp.querySelector(`#shopify-section-${headerSectionId}`) || temp.firstElementChild;
                if (!newSectionRoot) return;

                // 1) update cart drawer inner
                const newDrawerInner = newSectionRoot.querySelector('.cart-drawer__inner');
                const currentDrawerInner = document.querySelector('.cart-drawer__inner');
                if (newDrawerInner && currentDrawerInner) {
                  currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
                   // Open cart drawer
                          const cartDrawerrm = document.querySelector('.cart-drawer__dialog');

                        if (cartDrawerrm) {
                          cartDrawerrm.classList.remove('cart-drawer--empty');
                        }

                            const drawer = document.querySelector('cart-drawer-component');
                            if (drawer?.hasAttribute('auto-open')) {
                              drawer.open();
                            }
                }

                // 2) update header cart icon / bubble
                const newCartIcon = newSectionRoot.querySelector('.header-actions__cart-icon');
                const currentCartIcon = document.querySelector('.header-actions__cart-icon');
                if (newCartIcon && currentCartIcon) {
                  currentCartIcon.innerHTML = newCartIcon.innerHTML;
                        
                }
              });


  } catch (error) {
    alert('Error adding to cart!');
  }

  btn.disabled = false;
});

// Universal function
function disableBundleButtonssearch(container = '.predictive-search-form') {
  const containerEl = document.querySelector(container);
  if (!containerEl) return;
  
  const bundleBtns = containerEl.querySelectorAll('.resource-card__add-to-cart');
  console.log('Dynamic buttons found:', bundleBtns.length);
  
  bundleBtns.forEach(btn => {
    const hasApple = Array.from(btn.classList).some(cls => cls.includes('apple'));
    const hasWatermelon = Array.from(btn.classList).some(cls => cls.includes('watermelon'));
    
    if (hasApple || hasWatermelon) {
      const textEl = btn;
      if (textEl) {
        btn.classList.add('bundle-disabled');
          btn.disabled = true;
        textEl.textContent = 'Sold Out';
      }
    }
  });
}

// // 1. Initial load
// document.addEventListener('DOMContentLoaded', () => disableBundleButtonssearch());

// // 2. Shopify section reloads (theme editor/filtering)
// document.addEventListener('shopify:section:load', () => disableBundleButtonssearch());
// document.addEventListener('shopify:section:select', () => disableBundleButtonssearch());



function disableBundleButtonslp(params) {
  // Support both object AND positional args
  const container = params?.container || (typeof params === 'string' ? params : '.resource-list');
  const buttonClass = params?.buttonClass || '.bundle_add_to_cart';
  const textClass = params?.textClass || '.add-to-cart-text__content';
  const keywords = params?.keywords || ['apple', 'watermelon'];
 
  const containerEl = document.querySelector(container);
  if (!containerEl) return;
   
  containerEl.querySelectorAll(buttonClass).forEach(btn => {
      console.log(containerEl);
      console.log(buttonClass.length);
    const hasKeyword = keywords.some(kw => 
      Array.from(btn.classList).some(cls => cls.includes(kw))
    );
    
    if (hasKeyword) {
      const textEl = btn.querySelector(textClass);
      if (textEl) {
        btn.classList.add('bundle-disabled');
          btn.disabled = true;
        textEl.textContent = 'Sold Out';
      }
    }
  });
}
                                  

// disableBundleButtonslp(); 


function disableBundleButtonsfp(params) {
  // Support both object AND positional args
  const container = params?.container || (typeof params === 'string' ? params : '.featured-products-section');
  const buttonClass = params?.buttonClass || '.add-to-cart-button';
  const textClass = params?.textClass || '.add-to-cart-text__content';
  const keywords = params?.keywords || ['apple', 'watermelon'];
 
  const containerEl = document.querySelector(container);
  if (!containerEl) return;
   
  containerEl.querySelectorAll(buttonClass).forEach(btn => {
      console.log(containerEl);
      console.log(buttonClass.length);
    const hasKeyword = keywords.some(kw => 
      Array.from(btn.classList).some(cls => cls.includes(kw))
    );
    
    if (hasKeyword) {
      const textEl = btn.querySelector(textClass);
      if (textEl) {
        btn.classList.add('bundle-disabled');
          btn.disabled = true;
        textEl.textContent = 'Sold Out';
      }
    }
  });
}
                                  

// disableBundleButtonsfp(); 

                                 

function disableBundlebynow(params) {
  // Support both object AND positional args
  const container = params?.container || (typeof params === 'string' ? params : '.product-details');
  const buttonClass = params?.buttonClass || '.custom-buy-now';
  const textClass = params?.textClass || '.add-to-cart-text__content';
  const keywords = params?.keywords || ['apple', 'watermelon'];
 
  const containerEl = document.querySelector(container);
  if (!containerEl) return;
   
  containerEl.querySelectorAll(buttonClass).forEach(btn => {
      console.log(containerEl);
      console.log(buttonClass.length);
    const hasKeyword = keywords.some(kw => 
      Array.from(btn.classList).some(cls => cls.includes(kw))
    );
    
    if (hasKeyword) {
        btn.disabled = true;
        btn.classList.add('bundle-disabled');

      
    }
  });
}
                                  

// disableBundlebynow(); 


